import { useEffect, useState } from 'react';
import './css/Acp.css';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import PageCard from './components/PageCard';
import DefaultModal from './components/modals/DefaultModal';
import ContaModal from './components/modalServices/ContaModal';
import usePermissions from './components/hooks/usePermissions';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function Contas() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('contas');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  const [contas, setContas] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [modalData, setModalData] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showContaModal, setShowContaModal] = useState(false);
  const [contaToEdit, setContaToEdit] = useState(null);
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.CONTA}/all`, {
        params: { cnpjAssociacao: cnpj },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        const dadosRecebidos = Array.isArray(result.data) ? result.data : [];
        setContas(dadosRecebidos);
      })
      .catch(error => {
        console.error('Erro ao obter contas:', error);
        setContas([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [atualizar, token, cnpj, navigate, podeAbrir, carregandoPermissoes]);

  const totalItems = contas.length;
  const [pagination, setPagination] = useState({
    startIndex: 0,
    rowsPerPage: 10,
    currentPage: 1,
    onGoToPage: page => {
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        startIndex: (page - 1) * prev.rowsPerPage,
      }));
    },
  });

  const selectedContas = contas.slice(
    pagination.startIndex,
    pagination.startIndex + pagination.rowsPerPage
  );

  const showDefaultModal = (type, message) => {
    setModalData({ show: true, type: type, message: message });
  };
  const closeDefaultModal = () => {
    setModalData(prevModalData => ({ ...prevModalData, show: false }));
  };

  const handleSelectRecord = record => {
    const recordKey = `${record.cnpjAssociacao}-${record.numConta}`;
    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
      // Verificar se o programa está encerrado (se houver idPrograma)
      if (record.idPrograma) {
        const programaAtivo = isProgramaAtivo(record.idPrograma);
        setIsProgramaInativo(!programaAtivo);
      } else {
        setIsProgramaInativo(false);
      }
    }
  };

  const handleNewConta = () => {
    setContaToEdit(null);
    setShowContaModal(true);
  };

  const handleEditConta = () => {
    if (selectedRecord) {
      setContaToEdit(selectedRecord.data);
      setShowContaModal(true);
    }
  };

  const handleDeleteConta = async () => {
    if (!selectedRecord) return;
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(
        `${API_BASE_URL.CONTA}/delete/${selectedRecord.data.cnpjAssociacao}/${selectedRecord.data.numConta}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModalData({
        show: true,
        type: 'success',
        message: 'Conta excluída com sucesso!',
      });
      setAtualizar(prev => prev + 1);
      setSelectedRecord(null);
    } catch {
      setModalData({
        show: true,
        type: 'error',
        message: 'Erro ao excluir conta.',
      });
    }
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Contas Bancarias"
      icon="bi-bank2"
      scrollable={false}
      footer={
        contas.length > 0 ? (
          <Pagination10
            startIndex={pagination.startIndex}
            rowsPerPage={pagination.rowsPerPage}
            currentPage={pagination.currentPage}
            onGoToPage={pagination.onGoToPage}
            totalItems={totalItems}
          />
        ) : null
      }
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Contas.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            onClick={handleNewConta}
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar contas' : 'Nova conta'}
          >
            <i className="bi bi-plus-lg me-1" />
            Nova
          </button>
          <button
            onClick={handleEditConta}
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            disabled={!podeEditar || !selectedRecord || isProgramaInativo}
            title={!podeEditar ? 'Você não tem permissão para editar' : !selectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar registro selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <div className="vr mx-1" />
          <button
            onClick={handleDeleteConta}
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            disabled={!podeExcluir || !selectedRecord || isProgramaInativo}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !selectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível excluir' : 'Excluir registro selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>
          <div className="vr mx-1" />
          <button
            onClick={() => navigate('/contas/programas', { state: { cnpj: selectedRecord.data.cnpjAssociacao, numConta: selectedRecord.data.numConta } })}
            className="btn btn-info btn-sm d-inline-flex align-items-center text-white"
            disabled={!selectedRecord}
            title={!selectedRecord ? 'Selecione um registro' : 'Gerenciar associação de programa da conta'}
          >
            <i className="bi bi-gear me-1" />
            Gerenciar
          </button>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle" style={{ width: '30%' }}>
                  Conta
                </th>
                <th className="px-2 align-middle">Banco</th>
                <th className="px-2 align-middle">Agência</th>
                <th className='px-2 align-middle'>Descrição</th>
              </tr>
            </thead>
            <tbody>
              {!isLoading &&
                selectedContas.map(c => {
                  const recordKey = `${c.cnpjAssociacao}-${c.numConta}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;
                  const trStyle = isSelected
                    ? {
                        cursor: 'pointer',
                        backgroundColor: '#e3f2fd',
                        borderLeft: '4px solid #0d6efd',
                        boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)',
                      }
                    : { cursor: 'pointer' };
                  const tdStyle = isSelected
                    ? {
                        fontWeight: 'bold',
                        color: '#0d6efd',
                        backgroundColor: '#e3f2fd',
                      }
                    : {};
                  return (
                    <tr
                      key={recordKey}
                      style={trStyle}
                      onClick={() => handleSelectRecord(c)}
                    >
                      <td style={tdStyle}>{c.numConta}</td>
                      <td style={tdStyle}>{c.nomeBanco}</td>
                      <td style={tdStyle}>{c.agencia}</td>
                      <td style={tdStyle}>{c.descricao}</td>
                    </tr>
                  );
                })}
              {!isLoading && contas.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center">
                    Nenhuma conta encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

      </div>
      )}

      <DefaultModal
        show={modalData.show}
        type={modalData.type}
        onClose={closeDefaultModal}
        message={modalData.message}
      />

      {showContaModal && (
        <ContaModal
          show={showContaModal}
          onClose={() => setShowContaModal(false)}
          conta={contaToEdit}
          onSaveSuccess={() => {
            setShowContaModal(false);
            setAtualizar(prev => prev + 1);
          }}
        />
      )}

    </PageCard>
  );
}

export default Contas;
